import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Form, Formik } from 'formik';
import InputTypeSwitch from '../formElements/InputTypeSwitch';
import CancelButton from '../../buttons/CancelButton';
import SaveButton from '../../buttons/SaveButton';
import { getFormikInitialValues } from '../utility/formatData';
import { getValidationSchema } from '../utility/getValidationSchema';
import FormUIOptions from '../formElements/formUIOptions';
import classNames from 'classnames';
import Tooltip from '../../Tooltip';
import IconButton from '../../buttons/IconButton';

export default function EditItem({
  title,
  tableSchema,
  arrayHelpers,
  modalDescription,
  beforeArray,
  handleSave,
  listItem,
  setItem,
}) {
  const [isOpen, setIsOpen] = useState(true);

  const handleClose = () => {
    setIsOpen(false);
    setItem(null);
  };

  // Filter out fields that are excluded from new items, if this is a new item
  const tableSchemaFiltered = !beforeArray?.[listItem?.index]
    ? tableSchema.filter((field) => !field.excludeFromNewItem)
    : tableSchema;

  const initialData = listItem.item || listItem.afterItem || listItem;

  // If this is a new item, add a flag to the initial data to indicate that it is a new item (for use in the before/after table)
  // need to come up with a better way to handle this
  if (Array.isArray(beforeArray)) {
    if (!beforeArray[listItem?.index]) {
      initialData.unitRehab = true;
    }
  }

  const formikValues = getFormikInitialValues(
    Array.isArray(beforeArray)
      ? [...tableSchemaFiltered, { name: 'unitRehab' }]
      : tableSchemaFiltered,
    initialData
  );

  const validationSchema = getValidationSchema(tableSchemaFiltered);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-30 overflow-y-auto"
        static
        open={isOpen}
        onClose={() => handleClose()}
      >
        <div className="min-h-screen w-screen sm:px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300 opacity-30"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-100 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block max-w-3xl my-8 text-left align-middle transition-all transform bg-white sm:min-w-[23rem] shadow-xl rounded-lg px-4 relative">
              <div className="absolute top-0 left-0 p-5 mb-5 px-6 w-full rounded-t-lg flex items-center justify-between bg-white py-4 border-b border-neutral-50">
                <Dialog.Title
                  as="h3"
                  className="w-full text-4xl leading-6 font-medium text-neutral-900 flex"
                >
                  {title}
                  {modalDescription && (
                    <span className="text-black ml-2">
                      <Tooltip
                        text={modalDescription}
                        type="info"
                        iconColour="text-white"
                        iconSize="text-xl"
                        offset="top-8 -right-24"
                      />
                    </span>
                  )}
                </Dialog.Title>
              </div>

              <div className="px-2 py-4 mt-14">
                <Formik
                  initialValues={formikValues}
                  validateOnChange={false}
                  validationSchema={validationSchema}
                  onSubmit={(updates, { setSubmitting }) => {
                    handleSave(arrayHelpers, updates);
                  }}
                >
                  {({ isSubmitting, isValid }) => (
                    <Form className="grid grid-cols-4 gap-y-4 gap-x-6 items-start">
                      {tableSchemaFiltered.map((field, index) => (
                        <div
                          key={`input-${field.name}-${index}`}
                          className="col-span-4"
                        >
                          <InputTypeSwitch
                            elem={{
                              ...field,
                              layout: 'default',
                            }}
                            externalData={listItem?.item || listItem}
                          />
                        </div>
                      ))}
                      <div className="col-span-4 flex justify-between w-full gap-4 pt-4 mx-auto mt-2">
                        <IconButton
                          type="button"
                          style="outline-lg"
                          text="Cancel"
                          handleClick={() => handleClose()}
                        />
                        <IconButton
                          disabled={isSubmitting || !isValid}
                          type="submit"
                          style="primary-lg"
                          text="Save"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
